import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../config";
import "./Crm.css";

/**
 * The public enquiry form a prospect fills in — no login, branded with the
 * studio's logo. Submitting creates a lead on the studio's board.
 */
function EnquiryPublic() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [sent, setSent] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", email: "", event_type: "",
    event_date: "", venue: "", budget: "", message: "",
  });

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/crm/enquiry/${token}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "This form is not available.");
      // A 200 without a form body would otherwise leave a prospect staring at
      // "Loading…" forever; say something they can act on instead.
      if (!data.form) throw new Error("This enquiry form is not available right now.");
      setConfig(data.form);
    } catch (e) {
      setLoadError(e.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setError("Please leave a phone number or an email so we can reply.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/crm/enquiry/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Something went wrong. Please try again.");
      setSent(data.message);
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  };

  if (loadError) {
    return (
      <div className="qv-page">
        <div className="qv-sheet">
          <div className="crm-empty">
            <span className="crm-empty-icon">🔍</span>
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="qv-page">
        <div className="qv-sheet"><div className="crm-loading">Loading…</div></div>
      </div>
    );
  }

  return (
    <div className="qv-page">
      <div className="qv-sheet">
        <header className="qv-head" style={{ paddingBottom: 16 }}>
          <div className="qv-studio">
            {config.logo_url && (
              <img className="qv-logo" src={config.logo_url} alt={config.studio_name} />
            )}
            <div>
              <h1>{config.studio_name}</h1>
              <p>{config.headline || "Tell us about your event"}</p>
            </div>
          </div>
        </header>

        {sent ? (
          <div className="qv-status accepted" style={{ marginTop: 24 }}>
            ✅ {sent}
          </div>
        ) : (
          <>
            {config.intro && <p className="crm-enq-intro">{config.intro}</p>}
            {error && <div className="crm-form-error" style={{ marginTop: 14 }}>{error}</div>}
            <form onSubmit={submit} style={{ marginTop: 14 }}>
              <div className="crm-form-grid">
                <div className="crm-field">
                  <label htmlFor="enq-name">Your name *</label>
                  <input id="enq-name" value={form.name} onChange={set("name")} autoFocus />
                </div>
                <div className="crm-field">
                  <label htmlFor="enq-phone">Phone</label>
                  <input id="enq-phone" value={form.phone} onChange={set("phone")} inputMode="tel" />
                </div>
                <div className="crm-field">
                  <label htmlFor="enq-email">Email</label>
                  <input id="enq-email" type="email" value={form.email} onChange={set("email")} />
                </div>
                <div className="crm-field">
                  <label htmlFor="enq-event-type">Event type</label>
                  {config.event_types && config.event_types.length > 0 ? (
                    <select id="enq-event-type" value={form.event_type} onChange={set("event_type")}>
                      <option value="">— Select —</option>
                      {config.event_types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <input id="enq-event-type" value={form.event_type} onChange={set("event_type")}
                           placeholder="Wedding, Birthday…" />
                  )}
                </div>
                <div className="crm-field">
                  <label htmlFor="enq-date">Event date</label>
                  <input id="enq-date" type="date" value={form.event_date} onChange={set("event_date")} />
                </div>
                <div className="crm-field">
                  <label htmlFor="enq-venue">Venue / city</label>
                  <input id="enq-venue" value={form.venue} onChange={set("venue")} />
                </div>
                <div className="crm-field full">
                  <label htmlFor="enq-budget">Budget (optional)</label>
                  <input id="enq-budget" type="number" min="0" value={form.budget} onChange={set("budget")} />
                </div>
                <div className="crm-field full">
                  <label htmlFor="enq-message">Anything else we should know?</label>
                  <textarea id="enq-message" value={form.message} onChange={set("message")}
                            placeholder="Tell us about your plans…" />
                </div>
              </div>
              <div className="qv-actions">
                <button className="crm-btn primary qv-accept" type="submit" disabled={sending}>
                  {sending ? "Sending…" : "Send enquiry"}
                </button>
              </div>
            </form>
          </>
        )}

        <footer className="qv-foot">
          {config.studio_name} · Powered by Photentra
        </footer>
      </div>
    </div>
  );
}

export default EnquiryPublic;
